import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { PageHero } from "../components/Sections";
import { SITE, MARKETS, SERVICES, OFFICES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
    const [params] = useSearchParams();
    const intent = params.get("intent");
    const isAudit = intent === "audit";

    const [form, setForm] = useState({
        name: "",
        business: "",
        phone: "",
        email: "",
        city: params.get("city") || "",
        interest: isAudit ? "Growth Audit" : intent || "Growth Audit",
        message: "",
    });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        document.title = isAudit
            ? "Get a Growth Audit | ALBL Communications — Narmadapuram & Itarsi"
            : "Contact ALBL Communications | Brand & Sales Growth Partner, Narmadapuram";
    }, [isAudit]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please add your name and phone number.");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Enquiry sent. ALBL will get back to you shortly.");
            setForm({ ...form, name: "", business: "", phone: "", email: "", message: "" });
        } catch (err) {
            toast.error("Could not send right now. Please call or WhatsApp us instead.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full border border-slate-300 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0D131F] transition-colors";

    return (
        <main>
            <PageHero
                eyebrow={isAudit ? "Growth Audit" : "Contact"}
                title={
                    isAudit
                        ? "GET A GROWTH AUDIT."
                        : "TELL US WHERE YOUR BUSINESS IS TODAY."
                }
                sub="We'll help you identify where your marketing is leaving growth on the table."
            />

            <section className="bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28 grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <form onSubmit={submit} className="bg-white border border-slate-200 p-8 sm:p-12" data-testid="contact-form">
                                <Eyebrow>Send an enquiry</Eyebrow>
                                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="cf-name" className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                                            Your name *
                                        </label>
                                        <input id="cf-name" data-testid="contact-form-input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" required />
                                    </div>
                                    <div>
                                        <label htmlFor="cf-business" className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                                            Business name
                                        </label>
                                        <input id="cf-business" data-testid="contact-form-input-company" className={inputCls} value={form.business} onChange={set("business")} placeholder="Your business" />
                                    </div>
                                    <div>
                                        <label htmlFor="cf-phone" className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                                            Phone / WhatsApp *
                                        </label>
                                        <input id="cf-phone" data-testid="contact-form-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Your number" required />
                                    </div>
                                    <div>
                                        <label htmlFor="cf-email" className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                                            Email
                                        </label>
                                        <input id="cf-email" type="email" data-testid="contact-form-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                                    </div>
                                    <div>
                                        <label htmlFor="cf-city" className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                                            City / Market
                                        </label>
                                        <select id="cf-city" data-testid="contact-form-select-city" className={inputCls} value={form.city} onChange={set("city")}>
                                            <option value="">Select your market</option>
                                            {MARKETS.map((m) => (
                                                <option key={m.name} value={m.name}>{m.name}</option>
                                            ))}
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="cf-interest" className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                                            I&apos;m interested in
                                        </label>
                                        <select id="cf-interest" data-testid="contact-form-select-service" className={inputCls} value={form.interest} onChange={set("interest")}>
                                            <option value="Growth Audit">Growth Audit</option>
                                            <option value="Festival Sales Campaign">Festival Sales Campaign (Chaturthi · Navratri · Diwali)</option>
                                            {SERVICES.map((s) => (
                                                <option key={s.slug} value={s.title}>{s.title}</option>
                                            ))}
                                            <option value="General Enquiry">Something else</option>
                                        </select>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="cf-message" className="block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2">
                                            Where is your business today?
                                        </label>
                                        <textarea id="cf-message" rows={5} data-testid="contact-form-textarea-message" className={inputCls} value={form.message} onChange={set("message")} placeholder="What are you selling, and what is stopping growth right now?" />
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="contact-form-submit-button"
                                    className="btn-sharp mt-8 inline-flex items-center gap-2 bg-[#0D131F] text-white text-xs font-bold tracking-[0.15em] uppercase px-10 py-4 disabled:opacity-60"
                                >
                                    {sending ? "Sending…" : isAudit ? "Request Growth Audit" : "Send Enquiry"}
                                    <ArrowRight className="h-4 w-4 text-[#C5A059]" />
                                </button>
                            </form>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-5 space-y-4">
                        <Reveal delay={120}>
                            <a href={SITE.phoneHref} data-testid="contact-direct-phone" className="group flex items-center gap-5 bg-[#0D131F] text-white p-7 btn-sharp">
                                <Phone className="h-6 w-6 text-[#C5A059] shrink-0" />
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-slate-400">Call ALBL</p>
                                    <p className="font-display text-xl font-bold mt-1">{SITE.phone}</p>
                                </div>
                            </a>
                            <a href={SITE.whatsappPrefill} target="_blank" rel="noreferrer" data-testid="contact-direct-whatsapp" className="group flex items-center gap-5 bg-[#25D366] text-[#0D131F] p-7 btn-sharp">
                                <MessageCircle className="h-6 w-6 shrink-0" />
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase opacity-70">WhatsApp ALBL</p>
                                    <p className="font-display text-xl font-bold mt-1">Chat instantly</p>
                                </div>
                            </a>
                            <a href={SITE.emailHref} data-testid="contact-direct-email" className="group flex items-center gap-5 bg-white border border-slate-200 p-7 btn-sharp">
                                <Mail className="h-6 w-6 text-[#C5A059] shrink-0" />
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-slate-500">Email</p>
                                    <p className="font-display text-base font-bold text-slate-900 mt-1 break-all">{SITE.email}</p>
                                </div>
                            </a>
                            <div className="bg-white border border-slate-200 p-7">
                                <div className="flex items-center gap-5">
                                    <MapPin className="h-6 w-6 text-[#C5A059] shrink-0" />
                                    <div>
                                        <p className="text-xs tracking-[0.2em] uppercase text-slate-500">Markets served</p>
                                        <p className="text-sm text-slate-800 mt-1 leading-relaxed" data-testid="contact-address-narmadapuram">
                                            {MARKETS.map((m) => m.name).join(" · ")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white border border-slate-200 p-7 space-y-5">
                                {OFFICES.map((o) => (
                                    <div key={o.label} className="flex items-center gap-5">
                                        <MapPin className="h-6 w-6 text-[#C5A059] shrink-0" />
                                        <div>
                                            <p className="text-xs tracking-[0.2em] uppercase text-slate-500">{o.label}</p>
                                            <p className="text-sm text-slate-800 mt-1" data-testid={`contact-office-${o.label === "Registered Office" ? "delhi" : "bhopal"}`}>
                                                {o.value}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Contact;
